<template>
	<Page class="black-friday-paywall" :class="theme">
		<template v-slot:content>
			<SafeAreaTop closePosition="left" @close="onClose" :theme="closeTheme">
				<BlackFridayLogo :theme="theme" />
			</SafeAreaTop>
			<div class="black-friday-paywall__content container">
				<BlackFridayAdvantages class="black-friday-paywall__list" :theme="theme" />
				<PaywallSwitcher
					class="black-friday-paywall__switcher"
					label="Black Friday offer"
					v-model="applyDiscount"
					:theme="theme"
				/>
				<ProductRadioSelect :theme="theme" :products="products" :active="activeProduct" @select="handleSelect" />
				<div v-if="activeProduct.terms" class="black-friday-paywall__terms">{{ activeProduct.terms }}</div>
				<PaywallLinksBlock @restore="handleRestore" :theme="theme" />
			</div>
		</template>

		<template v-slot:footer>
			<div class="container">
				<Button @click="handleSubmit" class="btn black-friday-btn">Continue</Button>
			</div>
		</template>
	</Page>
</template>

<script>
import { inject, computed, ref, onMounted, watch } from 'vue'
import useApi from '@/composition/api.js'
import useConfig from '@/composition/config.js'

import Page from '@/components/Page'
import SafeAreaTop from '@/components/SafeAreaTop'
import Button from '@/components/Button.vue'
import BlackFridayLogo from '@/components/black-friday/Logo.vue'
import BlackFridayAdvantages from '@/components/black-friday/Advantages.vue'
import ProductRadioSelect from '@/components/ProductRadioSelect'
import { useStore } from 'vuex'
import PaywallSwitcher from '@/components/Switcher.vue'
import PaywallLinksBlock from '../../components/PaywallLinksBlock.vue'

export default {
	components: {
		Page,
		SafeAreaTop,
		Button,
		BlackFridayLogo,
		BlackFridayAdvantages,
		ProductRadioSelect,
		PaywallSwitcher,
		PaywallLinksBlock
	},

	setup() {
		const amplitude = inject('amplitude')
		const api = useApi()
		const config = useConfig()
		const store = useStore()

		const theme = computed(() => config.get({ key: 'paywall.theme', default_value: 'light' }))
		const closeTheme = computed(() => config.get({ key: 'paywall.close_button.theme', default_value: 'dark' }))

		const applyDiscount = ref(true)
		watch(
			() => applyDiscount.value,
			(val) => {
				activeProduct.value = products.value[0]
				amplitude.logEvent('BlackFridayPaywallToggleButtonClick', {
					action: val ? 'turn_on' : 'turn_off'
				})
			}
		)

		onMounted(() => {
			amplitude.logEvent(store.getters['getAmplitudeEventName']('PaywallShow'), {
				product_ids: products.value.map((p) => p?.id),
				prices: products.value.map((p) => p?.product_price),
				currency: products.value[0]?.currency?.toLowerCase(),
				data_source: 'client',
				offer: 'BlackFriday'
			})
		})

		const products = computed(() =>
			store.getters['products/getData']([
				applyDiscount.value
					? {
							id: 'simple_bf_subscription_3_month_discount',
							text: '12 weeks: [price]',
							badge: '50% OFF',
							terms:
								'Try 12 weeks with 50% Black Friday discount, then 29.99$ per 12-week (2.49$/ week). Cancel anytime.'
					  }
					: {
							id: 'simple_bf_subscription_3_month_wo_discount',
							text: '12 weeks: [price]',
							terms:
								'Subscription terms: a 12-week subscription fee is [price] and auto-renews each 12 weeks. Cancel anytime'
					  },
				{
					id: 'simple_bf_subscription_annual',
					text: 'Annually: [price]',
					terms: 'Subscription terms: an annual subscription [price] auto-renews yearly.'
				}
			])
		)
		const activeProduct = ref(products.value[0])

		const onClose = () => {
			amplitude.logEvent(store.getters['getAmplitudeEventName']('PaywallDone'), {
				action: 'close',
				offer: 'BlackFriday'
			})
			api.close()
		}

		const handleSelect = (product) => (activeProduct.value = product)

		const handleRestore = () =>
			api.restore({
				closeOnSuccess: true
			})

		const handleSubmit = () =>
			api.purchase({
				productId: activeProduct.value.id,
				onFinish: () => {
					amplitude.logEvent(store.getters['getAmplitudeEventName']('PaywallDone'), {
						action: 'Subscribe',
						product_id: activeProduct.value.id,
						duration_month: activeProduct.value.duration_months,
						price: Math.abs(activeProduct.value.product_price),
						currency: activeProduct.value.currency,
						offer: 'BlackFriday'
					})
				},
				onError: () => {
					console.log('error')
				},
				closeOnSuccess: true
			})

		return {
			onClose,
			theme,
			closeTheme,
			applyDiscount,
			products,
			activeProduct,
			handleSelect,
			handleRestore,
			handleSubmit
		}
	}
}
</script>

<style lang="scss" scoped>
.black-friday-paywall {
	&.light {
		--bg-color: #f5f6f8;
		--terms-color: #979aaa;
	}
	&.dark {
		--bg-color: #1c1e2c;
		--terms-color: #737587;
	}

	background-color: var(--bg-color);

	&__content {
		padding: 24px;
	}

	&__list {
		margin-bottom: 1.5rem;
	}

	&__switcher {
		margin-bottom: 2rem;
	}

	&__terms {
		color: var(--terms-color);
		font-size: 0.8rem;
		text-align: center;
		letter-spacing: -0.02rem;
		margin-top: 0.8rem;
	}

	:deep(.page-footer) {
		background: var(--bg-color);
		padding: 24px;
	}
}

.black-friday-btn {
	background: linear-gradient(266.94deg, #ff4f79 -9.75%, #9671ff 74.67%, #7d8bf7 104.63%),
		linear-gradient(270deg, #ff4f79 -8.75%, #9671ff 72.59%, #7d8bf7 101.46%);
	border-radius: 100px;
	color: #fff;
	width: 100%;
	border: none;
	letter-spacing: -0.02rem;
}
</style>
